import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { TooltipContainer } from './styles';
import leftIconNoArrow from '../../static/icons/leftIconNoArrow.svg';
import rightIconNoArrow from '../../static/icons/rightIconNoArrow.svg';
import {
    useFloating,
    offset,
    flip,
    shift,
    autoUpdate
} from '@floating-ui/react';
import { useHistory } from 'react-router-dom';
import { useTour } from '../../TourContext.jsx';

// This component is used to display the tour tooltip
const TourTooltip = ({
    text,
    tipCount = 3,
    currentStep,
    onNext,
    onSkip,
    onBack,
    style,
    reference,
    position,
    lastStepRedirectUrl,
    showOnboardingCompleteOnLastStep = true,
    // Default to 'fixed' so the portal'd tooltip (see render below) computes
    // viewport-relative coordinates and is unaffected by scrolling within the
    // page. Callers can still override to 'absolute' when needed.
    strategy = 'fixed',
    extraGap = false,
    /** When set, limits flip directions (e.g. avoid dropping the tooltip to the bottom of the viewport). */
    flipFallbackPlacements,
    /** Hide the Next button so the user has to interact with the
     *  highlighted target to advance (e.g. "Click Open" / "Click Duplicate"
     *  steps where Next would let the user bypass the demonstration). */
    hideNext = false,
    // --- Wizard tour (non-school teachers + students) opt-ins. All default to
    // the school-tour behaviour so existing callers are unaffected. ---
    /** Callout mode: render only the text (optionally with an icon) and NO Tip
     *  counter / Skip / Next chrome. Used by the wizard's "Submit Now" prompt,
     *  where the user advances by clicking the highlighted button itself. */
    calloutOnly = false,
    /** Optional icon (img src) shown before the text in callout mode. */
    calloutIcon,
    /** Label for the advance button (the wizard's last tip uses 'Finish'). */
    nextLabel = 'Next',
    /** Hide the "Skip all" link (the wizard's last tip shows only Finish). */
    hideSkip = false,
    /** Hide the Back button even when currentStep > 1 (wizard's last tip). */
    hideBack = false,
    /** Open the school setup checklist on Skip all. Defaults true (school
     *  tours); the wizard passes false so it can route to its own end state
     *  (plans modal / My Drafts) via onSkip instead. */
    showOnboardingCompleteOnSkip = true,
}) => {


    const { refs, floatingStyles, isPositioned } = useFloating({
        placement: position || 'bottom',
        middleware: [
            offset(typeof extraGap === 'number' ? extraGap : (extraGap ? 40 : 16)),

            flip(flipFallbackPlacements?.length ? { fallbackPlacements: flipFallbackPlacements } : {}),

            shift({
                padding: 8,
                crossAxis: true,
            }),
        ],
        // animationFrame: true re-reads the reference's getBoundingClientRect
        // every frame. The tour references are VIRTUAL elements that track a
        // scrolling target (see TourStepHighlight) — the default scroll/resize
        // listeners alone leave the tooltip trailing the target during scroll.
        whileElementsMounted: (referenceEl, floatingEl, update) =>
            autoUpdate(referenceEl, floatingEl, update, { animationFrame: true }),
        strategy,
    });

    const {
        setShowOnboardingComplete,
        setMarkingTourStep,
        setDraftTourStep,
        setNewTaskTourStep,
        setFeedbackTourStep,
        markTourAsComplete,
        finishCurrentTour,
        markingTourActive,
        draftTourActive,
        newTaskTourActive,
        feedbackTourActive,
        markingTourStep,
        draftTourStep,
        newTaskTourStep,
        feedbackTourStep
    } = useTour() || {};

    // Auto-resolve currentStep if not provided
    const resolvedCurrentStep = currentStep ?? (
        markingTourActive ? markingTourStep :
        draftTourActive ? draftTourStep :
        newTaskTourActive ? newTaskTourStep :
        feedbackTourActive ? feedbackTourStep : 1
    );

    // tipCount always resolves to the prop: the `tipCount = 3` destructuring
    // default means a `tipCount ?? (per-tour fallback)` branch here could
    // never run (and the constants it carried had drifted from the real
    // counts). Callers pass TIP_COUNTS from tours/tourCopy.
    const resolvedTipCount = tipCount;
    // Never display a step number past the total. If the counter ever overshoots
    // (e.g. rapid Next clicks before React commits — see handleNext below), the
    // raw value could read "Tip 15 of 13"; clamp it for display.
    const safeCurrentStep = Math.min(resolvedCurrentStep, resolvedTipCount);

    // Guard against a burst of Next clicks landing several functional step
    // increments in one React batch (which would skip steps or overshoot the
    // total). Allow exactly one advance per committed step; the effect re-arms
    // the guard whenever the step actually changes.
    const isAdvancing = React.useRef(false);
    React.useEffect(() => {
        isAdvancing.current = false;
    }, [resolvedCurrentStep]);

    const navigate = useHistory();

    React.useEffect(() => {
        if (!reference) return undefined;

        // Virtual element support
        if (reference.getBoundingClientRect) {
            refs.setReference(reference);
            return undefined;
        }

        const resolve = () => {
            const element = reference.current || reference;
            if (element instanceof HTMLElement) {
                refs.setReference(element);
                return true;
            }
            return false;
        };
        if (resolve()) return undefined;

        // Anchor not mounted yet: a rapid Next can advance the step while the
        // new step's target is still rendering (tab switch, drawer opening),
        // so the ref object's .current is null HERE — and a ref mutating
        // later never re-fires this effect. Without a retry the tooltip stays
        // isPositioned=false (visibility:hidden) forever while the tour
        // click-lock is armed: an invisible tooltip is an unescapable tour.
        // Poll briefly until the anchor appears; TourGuard's stranded-tour
        // watchdog is the fallback if it never does.
        const interval = setInterval(() => {
            if (resolve()) clearInterval(interval);
        }, 200);
        return () => clearInterval(interval);
    }, [reference, refs]);

    // Handle Next button click
    const handleNext = (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Drop repeat clicks until the step we just advanced to has committed.
        if (isAdvancing.current) return;
        isAdvancing.current = true;

        // `>=` not `===`: if the step counter ever overshoots the total (a burst
        // of Next clicks queues several increments before React commits, so each
        // sees the same pre-increment value and the counter lands past the last
        // step), strict equality would be false forever and the terminal branch
        // (markTourAsComplete + deactivate) would never fire — trapping the user
        // at "Tip N+x of N" with Next still showing and browser-back blocked.
        const isLastStep = resolvedCurrentStep >= resolvedTipCount;

        if (isLastStep) {
            // markTourAsComplete also flips onboardingTasks[3..6] so the
            // checklist reflects the completed tour (see TourContext
            // reducer). It's the "I really finished" signal.
            if (markingTourActive) markTourAsComplete('markingTour');
            if (draftTourActive) markTourAsComplete('draftTour');
            if (newTaskTourActive) markTourAsComplete('newTaskTour');
            if (feedbackTourActive) markTourAsComplete('feedbackTour');

            if (showOnboardingCompleteOnLastStep) {
                // OnboardingComplete renders as a modal overlay on whatever
                // page the user is on (no need to navigate). We deliberately
                // skip navigate.push('/tasks') here — the user stays at the
                // mirror /onboarding/* page underneath, with the checklist
                // overlay on top.
                setShowOnboardingComplete(true);
            }

            finishCurrentTour();
        } else {
            if (markingTourActive) setMarkingTourStep(prev => prev + 1);
            if (draftTourActive) setDraftTourStep(prev => prev + 1);
            if (newTaskTourActive) setNewTaskTourStep(prev => prev + 1);
            if (feedbackTourActive) setFeedbackTourStep(prev => prev + 1);
        }

        if (onNext) {
            onNext();
        }
    };

    // Handle Back button click
    const handleBack = (e) => {
        e.stopPropagation();

        if (markingTourActive) setMarkingTourStep(prev => Math.max(1, prev - 1));
        if (draftTourActive) setDraftTourStep(prev => Math.max(1, prev - 1));
        if (newTaskTourActive) setNewTaskTourStep(prev => Math.max(1, prev - 1));
        if (feedbackTourActive) setFeedbackTourStep(prev => Math.max(1, prev - 1));

        onBack?.();
    };

    // Handle Skip All — user is bailing on the tour mid-flow. Mark the
    // tour complete anyway: the user opened the tour, decided they didn't
    // want to walk every step, and tapped Skip — we don't heckle them by
    // leaving the corresponding checklist row unticked. Treating skip as
    // completion matches the user's intent ("I've seen enough, move on")
    // and avoids the second-pass nag where the row stays open even though
    // the user has already engaged with that feature.
    const handleSkipAll = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (markingTourActive) markTourAsComplete('markingTour');
        if (draftTourActive) markTourAsComplete('draftTour');
        if (newTaskTourActive) markTourAsComplete('newTaskTour');
        if (feedbackTourActive) markTourAsComplete('feedbackTour');

        finishCurrentTour();
        onSkip?.();
        if (showOnboardingCompleteOnSkip) {
            setShowOnboardingComplete(true);
        }
    };

    const baseStyles = {
        ...floatingStyles,
        width: 'max-content',
        maxWidth: 'min(300px, calc(100vw - 16px))',
        zIndex: 10006,
        pointerEvents: 'auto',
        opacity: isPositioned ? 1 : 0,
        visibility: isPositioned ? 'visible' : 'hidden',
        transition: 'opacity 0.2s ease-in-out',
    };

    // Portal the tooltip to document.body. Without this, the tooltip's
    // z-index:10006 is bounded by whatever stacking context its DOM ancestor
    // creates, and TourClickBlocker's portaled backdrop at z-index 10001
    // wins the hit-test — clicks on Next / Skip / Back never reach the
    // tooltip. Combined with strategy='fixed' above, the floating-ui
    // coordinates stay viewport-relative regardless of the new parent.
    //
    // Click-propagation guard (onClick + onMouseDown): React portals route
    // synthetic events through the React tree, NOT the DOM tree. The
    // tooltip is typically rendered as a child of a clickable element
    // (e.g. MainSidebar's <li onClick={handlePageRoute}>). Without
    // stopPropagation, clicking the tooltip body — which has no native
    // handler — bubbles up the React parent chain to that <li>, fires a
    // history.push, and TourGuard surfaces TourExitWarning thinking the
    // user is trying to leave the tour. Stopping propagation here keeps
    // the Next / Back / Skip handlers (each call stopPropagation
    // themselves) working while neutralising clicks on the rest of the
    // tooltip surface. onMouseDown covers the same path for elements
    // that respond on mousedown rather than click.
    const stopBubble = (e) => e.stopPropagation();
    const tooltipNode = (
        <TooltipContainer
            style={baseStyles}
            ref={refs.setFloating}
            onClick={stopBubble}
            onMouseDown={stopBubble}
            // Stable hook for TourClickBlocker's keyboard-trap exemption.
            // styled-components class names get hashed in production builds,
            // so a className-based selector can't reliably match. The data
            // attribute is preserved verbatim across builds.
            data-tour-tooltip="true"
        >
            {calloutOnly ? (
                <div
                    className="callout"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    {calloutIcon && (
                        <img
                            src={calloutIcon}
                            alt=""
                            style={{ width: 20, height: 20, flexShrink: 0 }}
                        />
                    )}
                    <p style={{ margin: 0 }}>{text}</p>
                </div>
            ) : (
                <>
                    <p>{text}</p>
                    <div className="actions">
                        <div className="tip-info">
                            {!hideBack && resolvedCurrentStep > 1 && (
                                <div className="back-btn" onClick={handleBack}>
                                    <img src={leftIconNoArrow} alt="back" />
                                </div>
                            )}
                            <span className="tip-text">
                                Tip {safeCurrentStep} of {resolvedTipCount}
                            </span>
                        </div>
                        <div className="main-actions">
                            {!hideSkip && (
                                <p className="subtext" onClick={handleSkipAll}>
                                    Skip all
                                </p>
                            )}
                            {/* Reserve the Next button's footprint even when hidden,
                                so Skip All doesn't slide right into the slot the user
                                was about to click. visibility:hidden keeps the layout
                                slot; pointer-events:none on the placeholder makes
                                sure stray clicks don't hit the invisible button. */}
                            {hideNext ? (
                                <button
                                    type="button"
                                    aria-hidden="true"
                                    tabIndex={-1}
                                    className="next-btn"
                                    style={{ visibility: 'hidden', pointerEvents: 'none' }}
                                >
                                    {nextLabel}
                                    <img src={rightIconNoArrow} alt="" />
                                </button>
                            ) : (
                                <button type="button" onClick={handleNext} className="next-btn">
                                    {nextLabel}
                                    <img src={rightIconNoArrow} alt="next" />
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}
        </TooltipContainer>
    );

    if (typeof document === 'undefined') return tooltipNode;
    return createPortal(tooltipNode, document.body);
}

export default TourTooltip;